import {
  allMantraBucketKeys,
  classicBucketsForPlayer,
  CLASSIC_GROUPS,
  mantraBucketsForPlayer,
  normalizePlayerRoles,
} from './roleBuckets.js';
import { collectMarketEvents } from './analyticsPriceTrend.js';
import { MANTRA_ROLES } from './studioCalc.js';
import { sumRosterSpent } from './rosaCalc.js';

export const TOP_AUCTION_PRICES = 10;

function badgeBucketForPlayer(player, mode) {
  const roles = normalizePlayerRoles(player);
  if (!roles.length) return null;

  if (mode === 'mantra') {
    if (roles.length === 1) return roles[0];
    for (const r of MANTRA_ROLES) {
      if (roles.includes(r)) return `${r}+`;
    }
    return null;
  }

  const buckets = classicBucketsForPlayer(player);
  for (const g of CLASSIC_GROUPS) {
    if (buckets.includes(g)) return g;
  }
  return null;
}

function compareVs(price, marketAvg) {
  if (marketAvg == null) return 'na';
  if (price > marketAvg) return 'above';
  if (price < marketAvg) return 'below';
  return 'equal';
}

function isValidComparePrice(price) {
  const n = Number(price);
  return Number.isFinite(n) && n >= 1;
}

function marketAvgByBucketFromEvents(marketEvents, { allBuckets, bucketsForPlayer, playersByKey }) {
  const agg = new Map(
    allBuckets.map((bucket) => [bucket, { sum: 0, count: 0 }]),
  );

  for (const event of marketEvents) {
    const player = playersByKey.get(event.key);
    const buckets = bucketsForPlayer(player ?? { roles: [] });
    for (const bucket of buckets) {
      const a = agg.get(bucket);
      if (!a) continue;
      a.sum += event.price;
      a.count += 1;
    }
  }

  return new Map(
    allBuckets.map((bucket) => {
      const a = agg.get(bucket);
      const avg = a.count > 0 ? Math.round(a.sum / a.count) : null;
      return [bucket, avg];
    }),
  );
}

export function computeAnalyticsSummary({
  mode,
  budget,
  maxPlayers,
  rosterEntries = [],
  exitEntries = [],
  playersByKey = new Map(),
  marketEvents: marketEventsInput,
  /** Acquisti della rosa in focus (tu o un avversario). Default: rosterEntries. */
  situationEntries = null,
  /** Acquisti da confrontare con la media asta. Default: roster (mine). */
  compareEntries = null,
  /** Eventi per la media asta del confronto. Default: marketEvents. */
  compareMarketEvents = null,
}) {
  const forSituation = Array.isArray(situationEntries) ? situationEntries : rosterEntries;
  const spent = sumRosterSpent(forSituation);
  const owned = forSituation.length;
  const slots = maxPlayers;
  const residui = budget - spent;
  const open = Math.max(0, slots - owned);
  const perOpenSlot = open === 0 ? null : Math.round(residui / open);

  const allBuckets = mode === 'mantra' ? allMantraBucketKeys() : CLASSIC_GROUPS;
  const bucketsForPlayer =
    mode === 'mantra' ? mantraBucketsForPlayer : classicBucketsForPlayer;

  const agg = new Map(
    allBuckets.map((bucket) => [bucket, { sum: 0, count: 0, min: null, max: null }]),
  );

  const marketEvents = marketEventsInput ?? collectMarketEvents({ rosterEntries, exitEntries });

  for (const event of marketEvents) {
    const player = playersByKey.get(event.key);
    const buckets = bucketsForPlayer(player ?? { roles: [] });
    for (const bucket of buckets) {
      const a = agg.get(bucket);
      if (!a) continue;
      a.sum += event.price;
      a.count += 1;
      a.min = a.min === null ? event.price : Math.min(a.min, event.price);
      a.max = a.max === null ? event.price : Math.max(a.max, event.price);
    }
  }

  const marketRows = allBuckets.map((bucket) => {
    const a = agg.get(bucket);
    const count = a.count;
    const avg = count > 0 ? Math.round(a.sum / count) : null;
    return {
      bucket,
      count,
      avg,
      min: count > 0 ? a.min : null,
      max: count > 0 ? a.max : null,
    };
  });

  const marketAvgByBucket = new Map(marketRows.map((r) => [r.bucket, r.avg]));
  const compareBaselineEvents = compareMarketEvents ?? marketEvents;
  const marketAvgForCompare = marketAvgByBucketFromEvents(compareBaselineEvents, {
    allBuckets,
    bucketsForPlayer,
    playersByKey,
  });

  const top = [...marketEvents]
    .sort((a, b) => b.price - a.price)
    .slice(0, TOP_AUCTION_PRICES)
    .map((event) => {
      const player = playersByKey.get(event.key);
      return {
        key: event.key,
        name: player?.name ?? event.key,
        price: event.price,
        buckets: bucketsForPlayer(player ?? { roles: [] }),
        mine: event.mine === true,
      };
    });

  const focusCompareEntries = Array.isArray(compareEntries)
    ? compareEntries.filter((entry) => isValidComparePrice(entry.price))
    : marketEvents.filter((event) => event.mine);
  const focusBucketsSet = new Set();
  const focusAgg = new Map();

  for (const entry of focusCompareEntries) {
    const price = Number(entry.price);
    const player = playersByKey.get(entry.key);
    const buckets = bucketsForPlayer(player ?? { roles: [] });
    for (const bucket of buckets) {
      focusBucketsSet.add(bucket);
      if (!focusAgg.has(bucket)) focusAgg.set(bucket, { sum: 0, count: 0 });
      const a = focusAgg.get(bucket);
      a.sum += price;
      a.count += 1;
    }
  }

  const compareRows = allBuckets
    .filter((bucket) => focusBucketsSet.has(bucket))
    .map((bucket) => {
      const focus = focusAgg.get(bucket);
      const myAvg = focus.count > 0 ? Math.round(focus.sum / focus.count) : null;
      const marketAvg = marketAvgForCompare.get(bucket);
      const delta =
        myAvg != null && marketAvg != null ? myAvg - marketAvg : null;
      return { bucket, myAvg, marketAvg, delta };
    });

  const myPlayers = focusCompareEntries.map((entry) => {
    const price = Number(entry.price);
    const player = playersByKey.get(entry.key);
    const bucket = badgeBucketForPlayer(player ?? { roles: [] }, mode);
    const marketAvg = bucket ? marketAvgForCompare.get(bucket) : null;
    return {
      key: entry.key,
      name: player?.name ?? entry.key,
      price,
      bucket,
      vs: compareVs(price, marketAvg),
    };
  });

  return {
    situation: { spent, residui, owned, slots, perOpenSlot },
    market: { rows: marketRows, top },
    compare: { rows: compareRows, myPlayers },
  };
}
